import Text from 'components/base/Text';
import { Flex, Separator } from 'components/base/layout-components';
import { getColor } from 'common/constants/_colors';
import styled from 'styled-components';
import {
  ConnectedState,
  NotActiveState,
  NotConnectedState
} from '../../authStateBar/connectedAuthStates';
import {
  AUTH_STATE_STATUSES,
  mapAuthStatusToStyleProps
} from '../../authStateBar/authStateBar.common';
import { useSelector } from 'react-redux';
import {
  activeGitIntegrationsSelector,
  gitConnectedSensorIdsSelector,
  gitIntegrationsSelector
} from 'state/selectors/sensors-authorizations';
import { pluralize } from 'common/utils/utils';
import { GIT_PROVIDERS } from 'common/constants/constants';
import { licenseSelector } from 'state/selectors/license';
import MaxReposIndicator from './MaxReposIndicator';
import {
  LBAzureIcon,
  LBBitbucketIcon,
  LBGithubIcon,
  LBGitlabIcon
} from '@linearb/buzz';

const AuthStateWrapper = styled(Flex)`
  background-color: ${({ bgColor }) => `${getColor(bgColor)}`};
  border: 1px solid ${({ borderColor }) => getColor(borderColor)};
`;

const mapGitSensorIdsToUI = {
  [GIT_PROVIDERS.GITHUB]: {
    name: 'github',
    logo: LBGithubIcon
  },
  [GIT_PROVIDERS.GITLAB]: {
    name: 'gitlab',
    logo: LBGitlabIcon
  },
  [GIT_PROVIDERS.BITBUCKET]: {
    name: 'bitbucket',
    logo: LBBitbucketIcon
  },
  [GIT_PROVIDERS.BITBUCKET_SERVER]: {
    name: 'bitbucket server',
    logo: LBBitbucketIcon
  },
  [GIT_PROVIDERS.AZURE_DEVOPS]: {
    name: 'azure devops',
    logo: LBAzureIcon
  }
};

const getAuthStateStatus = (isNotConnected, isNotActive) => {
  if (isNotConnected) return AUTH_STATE_STATUSES.NOT_CONNECTED;
  if (isNotActive) return AUTH_STATE_STATUSES.INACTIVE;
  return AUTH_STATE_STATUSES.CONNECTED;
};

const GitAuthState = () => {
  const license = useSelector(licenseSelector);
  const reposLeft = license?.reposLeft;

  const gitIntegrations = useSelector(gitIntegrationsSelector);
  const activeGitIntegrations = useSelector(activeGitIntegrationsSelector);
  const connectedGitSensorIds = useSelector(gitConnectedSensorIdsSelector);
  const connectedGitProviders = connectedGitSensorIds.map(
    sensorId => mapGitSensorIdsToUI[sensorId]
  );

  const isNotConnected = gitIntegrations.length === 0;
  const isNotActive = !isNotConnected && activeGitIntegrations.length === 0;
  const authStateStatus = getAuthStateStatus(isNotConnected, isNotActive);

  const connectedTopBarConfig = mapAuthStatusToStyleProps[authStateStatus];

  return (
    <AuthStateWrapper
      items='center'
      grow='1'
      padding='small'
      mb='medium'
      minHeight='3.4rem'
      justify='space-between'
      bgColor={connectedTopBarConfig.backgroundColor}
      borderColor={connectedTopBarConfig.border}
    >
      <Flex items='center' grow='1'>
        <div className='flex flex-row items-center gap-4'>
          {connectedGitProviders.map(({ logo: Logo, name }) => (
            <Logo className='size-6 last:mr-2' key={name} />
          ))}
        </div>
        <Text size='x-large'>Git</Text>
        {authStateStatus === AUTH_STATE_STATUSES.CONNECTED && (
          <>
            {/* @ts-ignore */}
            <ConnectedState />
          </>
        )}
        {authStateStatus === AUTH_STATE_STATUSES.NOT_CONNECTED && (
          <NotConnectedState />
        )}
        {authStateStatus === AUTH_STATE_STATUSES.INACTIVE && <NotActiveState />}
        <Separator height='2rem' margin='0 2rem' />
        <Text size='medium' color='shuttleGray'>
          {pluralize(activeGitIntegrations.length, 'active integration')}
        </Text>
        {reposLeft <= 0 && <MaxReposIndicator />}
      </Flex>
    </AuthStateWrapper>
  );
};

export default GitAuthState;
